import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from 'react-query';
import { Link, useParams } from "react-router-dom";
import moment from "moment";
import { useSnackbar } from "notistack";
import SweetAlert from 'react-bootstrap-sweetalert';
import ReactTooltip from 'react-tooltip'
import 'react-datepicker/dist/react-datepicker.css';

import NTMButton from "components/Buttons/NTMButton";
import Image from "components/Image";
import { Column, Row } from "components/Layout";
import HereMap from "components/Map/HereMap";
import NTMTable from "components/Table/NTMTable";

import useCurrentPosition from "hooks/useCurrentPosition";
import { useGetDevice, useGetDeviceInstallations } from "service/RadarpestService";
import { deleteElem, getErrorMessage, post, put } from "../../service/Client";
import { addIcon_top, addIcon_topOver, deleteIcon, deleteIcon_over, editIcon, editIcon_over, focusIcon, focusIcon_over, menuIcons, pinIcons } from 'img/icons';
import { roles } from "../../constants";
import style from "./device.module.scss";
import TrapInstallationModal from "components/Modal/TrapInstallationModal";
import InfoCell from "components/Layout/InfoCell";


export default function RadarpestDevicePage() {

	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	let user = useSelector(store => store.users.data);
	let { deviceid: deviceId } = useParams()
	const { device } = useGetDevice(deviceId)
	const { installations } = useGetDeviceInstallations(deviceId)
	const position = useCurrentPosition()

	const [alert, setAlert] = useState()
	const [installation, setInstallation] = useState()
	const [installationAction, setInstallationAction] = useState()
	const [center, setCenter] = useState()

	const showError = useCallback(e => {
		enqueueSnackbar(getErrorMessage(e), { variant: "error" });
	}, [enqueueSnackbar])

	const submitInstallationInfo = useCallback((installationAction, installation) => {
		switch (installationAction) {
			case 'create':
				post(`radarpest/installation`, {
					params: { did: installation.deviceSerial },
					body: installation
				})
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
					.catch(showError)
				break
			case 'update':
				put(`radarpest/installation/${installation.id}`, { body: installation })
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
					.catch(showError)
				break
			default:
				break
		}
		setInstallationAction(null)
	}, [queryClient, showError])

	function removeInstallation(installation) {
		setAlert(<SweetAlert
			warning
			showCancel
			cancelBtnText={"Annulla"}
			confirmBtnText="Ho capito, esegui"
			confirmBtnBsStyle="primary"
			title="Confermi?"
			onConfirm={() => {
				put(`radarpest/installation/${installation.id}`, { body: { removalTime: moment().unix() * 1000 } })
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
					.catch(showError)
					.finally(() => setAlert(null))
			}}
			onCancel={() => setAlert(null)}
			focusCancelBtn
		>
			{`L'installazione ${installation.id} sarà terminata`}
		</SweetAlert>)
	}

	function deleteInstallation(installation) {
		setAlert(<SweetAlert
			warning
			showCancel
			cancelBtnText={"Annulla"}
			confirmBtnText="Ho capito, esegui"
			confirmBtnBsStyle="danger"
			title="Confermi?"
			onConfirm={() => {
				deleteElem(`radarpest/installation/${installation.id}`)
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
					.catch(showError)
					.finally(() => setAlert(null))
			}}
			onCancel={() => setAlert(null)}
			focusCancelBtn
		>
			{`L'installazione ${installation.id} sarà eliminata`}
		</SweetAlert>)
	}


	function installationPopUp(installationId, deviceId, installationTime, removalTime) {
		return `<div class="bubble">
					<div class="name">id installazione</div><div>${installationId}</div>
					<div class="name">id centralina</div><div>${deviceId}</div>
					<div class="name">Data installazione</div>
					<div>${installationTime ? moment(installationTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
					<div class="name">Data rimozione</div>
					<div>${removalTime ? moment(removalTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
				</div>`;
	}

	let markers = [];
	if (position && position.latitude && position.longitude) {
		markers.push({
			lat: position.latitude,
			lng: position.longitude,
			data: `<div class="bubble">Posizione corrente</div>`,
			icon: pinIcons.azure
		})
	}
	installations.forEach(k => {
		if (k.lat && k.lng) markers.push({
			lat: k.lat,
			lng: k.lng,
			data: installationPopUp(k.id, k.deviceSerial, k.installationTime, k.removalTime),
			icon: k.removalTime ? pinIcons.red : pinIcons.green
		});
	});

	let mapCenter = center || ((markers && markers.length > 0) && { lat: markers[0].lat, lng: markers[0].lng })

	let tableHeaderInstallation = [
		{
			Header: "ID",
			accessor: 'id',
			filterable: true,
			Cell: props => <span style={{ paddingRight: "0.5rem" }}>{props.original.id}</span>
		},
		{
			Header: "ID centralina",
			accessor: 'deviceSerial',
			filterable: true,
			Cell: props => <span style={{ paddingRight: "0.5rem" }}>{props.original.deviceSerial}</span>
		},
		{
			Header: "Installazione",
			accessor: 'installationTime',
			Cell: props =>
				<div>{props.original.installationTime ? moment(props.original.installationTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Rimozione",
			accessor: 'removalTime',
			Cell: props =>
				<div>{props.original.removalTime ?
					moment(props.original.removalTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") :
					roles.isAdmin(user) &&
					<Row style={{ justifyContent: "space-around" }}>
						<NTMButton primary onClick={() => removeInstallation(props.original)}>Rimuovi</NTMButton>
					</Row>
				}</div>
		},
		{
			Header: "Trappola 1",
			accessor: 'description1',
			filterable: true,
			Cell: props => <div>{props.original.description1}</div>
		},
		{
			Header: "Trappola 2",
			accessor: 'description2',
			filterable: true,
			Cell: props => <div>{props.original.description2}</div>
		},
		{
			Header: "Ultima ril",
			accessor: 'receiptDate',
			Cell: props =>
				<div>{props.original.receiptDate ? moment(props.original.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Cont. 1",
			accessor: 'insectCount1',
			Cell: props => <div>{`${+props.original.insectCount1 || "-"}`}</div>
		},
		{
			Header: "Cont. 2",
			accessor: 'insectCount2',
			Cell: props => <div>{`${+props.original.insectCount2 || "-"}`}</div>
		},
		{
			Header: "",
			accessor: 'controls',
			Cell: props => <Column>
				<ReactTooltip effect='solid' />
				<Row style={{ justifyContent: 'space-around' }}>
					<div data-tip="Centra sulla mappa">
						<Image
							src={focusIcon}
							hover={focusIcon_over}
							onClick={() => props.original.lat && props.original.lng
								&& setCenter({ lat: props.original.lat, lng: props.original.lng })}
						/>
					</div>
					<Link
						to={`/trapinst/${props.original.id}/charts`}
						data-tip="Vai ai grafici"
					>
						<Image
							src={menuIcons.chart}
							hover={menuIcons.chart_over}
						/>
					</Link>
					<Link
						to={`/trapinst/${props.original.id}/data`}
						data-tip="Vai ai dati"
					>
						<Image
							src={menuIcons.data}
							hover={menuIcons.data_over}
						/>
					</Link>
				</Row>
				{roles.isAdmin(user) && <Row style={{ justifyContent: 'space-around' }}>
					<div data-tip="Modifica">
						<Image
							src={editIcon}
							hover={editIcon_over}
							onClick={() => {
								setInstallation(props.original)
								setInstallationAction('update')
							}}
						/>
					</div>
					<div data-tip="Elimina">
						<Image
							src={deleteIcon}
							hover={deleteIcon_over}
							onClick={() => deleteInstallation(props.original)}
						/>
					</div>
				</Row>}
			</Column>
		}
	]

	return (
		<div className={"animated fadeIn container"}>
			<Row className={style.box}>
				<Column>
					<HereMap
						center={mapCenter || { lat: 40.967320, lng: 17.115670 }}
						zoom={10}
						theme={"normal.day"}
						markers={markers}
						className={style.map}
					/>
				</Column>
				<Column>
					<div className={style.infoGrid}>
						<InfoCell name="id dispositivo" value={device?.id} />
						<InfoCell name="seriale sigfox" value={device?.deviceId} />
						<InfoCell name="label" value={device?.label} />
						<InfoCell name="Nome Progetto" value={device?.projectName} />
						<InfoCell name="Id Installazione attiva" value={device?.activeInstallation?.id} />
					</div>
				</Column>
			</Row>

			{roles.isAdmin(user) &&
				<Row style={{ paddingLeft: "1rem" }}>
					<NTMButton onClick={() => {
						if (!device.activeInstallation) {
							setInstallation(null)
							setInstallationAction('create')
						} else {
							setAlert(<SweetAlert
								type={'info'}
								title={'Info'}
								onConfirm={() => setAlert(null)}
								onCancel={() => setAlert(null)}
							>
								Centralina non disponibile per una nuova installazione.<br/>
								Terminare prima l'installazione corrente.
							</SweetAlert>)
						}
					}}>
						<Image
							src={addIcon_top}
							hover={addIcon_topOver}
						/>
						&nbsp;&nbsp;Nuova installazione
					</NTMButton>
				</Row>
			}
			<Row>
				{
					installations.length === 0 &&
					<Row style={{ color: "#D8232A", paddingBottom: "1rem", fontWeight: "bold" }}>
						Non è ancora stata associata nessuna installazione a questo progetto
					</Row>
				}
				{
					installations.length > 0 &&
					<NTMTable
						title="Installazioni"
						header={tableHeaderInstallation}
						body={installations}
						defaultPageSize={5}
					/>
				}
			</Row>

			<TrapInstallationModal
				mode={installationAction}
				onClose={() => setInstallationAction(null)}
				onSubmit={submitInstallationInfo}
				installation={installation}
				freeDevices={[device]}
				position={position}
				canSelectDevice={false}
			/>
			{alert}

		</div>
	);

}
